import { useState, useEffect, useRef } from "react"
import { Eye, EyeOff, AlertCircle } from "lucide-react"
import { useLocation, useNavigate } from "react-router-dom"
import axiosInstance from "../../../api/Axiosinstance"
import { useToast } from "../../../hooks/useToast"

const symbolRegex = /[!@#$%^&*(),.?":{}|<>]/
const numberRegex = /\d/

export default function ResetPassword() {
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const navigate = useNavigate()
  const location = useLocation()
  const redirectTimer = useRef(null)
  const toast = useToast()

  useEffect(() => {
    return () => {
      if (redirectTimer.current) clearTimeout(redirectTimer.current)
    }
  }, [])

  const searchParams = new URLSearchParams(location.search)
  const hashParams = new URLSearchParams(location.hash.replace(/^#/, ""))
  const tokenKeys = ["token", "code", "reset_token", "resetToken", "otp"]
  const getResetToken = (params, fallback) => {
    for (const key of tokenKeys) {
      const value = params.get(key)
      if (value) return { key, value: value.trim() }
    }
    for (const [key, value] of params.entries()) {
      if (tokenKeys.includes(key.toLowerCase()) && value) {
        return { key, value: value.trim() }
      }
    }
    if (fallback) {
      for (const key of tokenKeys) {
        const regex = new RegExp(`[?#&]${key}=([^&#]+)`, "i")
        const match = fallback.match(regex)
        if (match?.[1]) {
          return { key, value: decodeURIComponent(match[1]).trim() }
        }
      }
    }
    return null
  }
  const searchToken = getResetToken(searchParams, location.search)
  const hashToken = getResetToken(hashParams, location.hash)
  const resetToken = searchToken || hashToken || { key: "token", value: "" }
  const tokenValue = resetToken.value


  const passwordWeak =
    password.length > 0 && (!numberRegex.test(password) || !symbolRegex.test(password) || password.length < 8)
  const passwordError = passwordWeak ? "Password strength - weak" : ""
  const passwordsMatch = confirmPassword.length > 0 && password === confirmPassword
  const confirmError = confirmPassword.length > 0 && !passwordsMatch ? "Passwords do not match" : ""
  const isFormValid = Boolean(tokenValue) && !passwordWeak && passwordsMatch

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!tokenValue) {
      toast.error("Reset link is invalid or expired. Please request a new one.")
      return
    }

    if (passwordWeak || !passwordsMatch) {
      return
    }

    setSubmitting(true)
    try {
      // Backend endpoint (currently failing): POST /auth/reset-password
      await axiosInstance.post("/auth/reset-password", {
        token: tokenValue,
        password,
        confirmPassword,
      })
      toast.success("Password reset successful! Redirecting to login...")
      redirectTimer.current = setTimeout(() => navigate("/auth/login"), 2500)
    } catch (err) {
      // Rate limiting lives in the status code, not the body, so it stays a
      // special case; everything else goes through the shared extractor.
      if (err.response?.status === 429) {
        toast.error(
          err.response?.data?.message ||
            "Too many password reset attempts. Please wait a few minutes and try again."
        )
      } else {
        toast.apiError(err, "Unable to reset password.")
      }
    } finally {
      setSubmitting(false)
    }
  }

  const inputBase =
    "w-full rounded-lg border px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none transition focus:ring-2 focus:ring-offset-0"
  const errorInput = "border-red-300 focus:border-red-500 focus:ring-red-200"
  const normalInput = "border-gray-300 focus:border-[#7a0051] focus:ring-[#7a0051]/30 shadow-sm"

  return (
    <div className="flex min-h-screen items-center justify-center bg-white px-4 text-gray-900">
      <div className="w-full max-w-lg space-y-8 text-center">

        <div className="flex justify-center">
          <img src="/images/logo.png" alt="PULO Logo" className="w-20" />
        </div>

        <div className="space-y-2">
          <h1 className="text-4xl font-semibold">Reset Password</h1>
          <p className="text-sm text-gray-600 font-medium">Create a new password you can remember easily</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 text-left">
          <div>
            <label className="mb-2 block text-sm  text-gray-800">Password</label>
            <div className={`relative rounded-lg ${passwordError ? "ring-1 ring-red-200" : "ring-1 ring-transparent"}`}>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                }}
                placeholder="Enter new password"
                className={`${inputBase} pr-12 ${passwordError ? errorInput : normalInput}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {passwordError && (
              <p className="mt-2 flex items-center gap-1 text-sm text-red-600">
                <AlertCircle className="h-4 w-4" />
                {passwordError}
              </p>
            )}
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-800">Repeat Password</label>
            <div className={`relative rounded-lg ${confirmError ? "ring-1 ring-red-200" : "ring-1 ring-transparent"}`}>
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value)
                }}
                placeholder="Re-enter new password"
                className={`${inputBase} pr-12 ${confirmError ? errorInput : normalInput}`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-500 hover:text-gray-700"
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {confirmError && (
              <p className="mt-2 flex items-center gap-1 text-sm text-red-600">
                <AlertCircle className="h-4 w-4" />
                {confirmError}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting || !isFormValid}
            className="w-full rounded-lg bg-[#7a0051] py-3 text-base font-semibold text-white shadow-md transition hover:bg-[#640140] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7a0051] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? "Please wait..." : "Continue"}
          </button>
        </form>
      </div>
    </div>
  )
}
